import { Controller } from "@hotwired/stimulus"

import { getConsumer } from "@shared/lib/action-cable/consumer"

// Subscribes to SupportChannel for tenant-wide ticket content events
// (ticket_created, comment_added, status_changed, ai_*). On list-affecting
// events, reloads the `tickets-list` Turbo Frame; on detail-affecting events
// (comment, status, assignment, AI triage/summary) for the ticket the user is
// viewing, reloads the `ticket-detail` Turbo Frame. Frame reloads preserve
// filters, search input, scroll position, and form state — a full
// `window.location.reload()` is only used as a fallback when the targeted
// frame is not present on the page.
//
// Presence (viewing avatars + typing indicator) is handled by a separate
// per-ticket subscription owned by `support--presence`.
//
// data-controller="support--live-updates"
const RELOAD_DEBOUNCE_MS = 300

export default class extends Controller {
  static values = { userId: String, spaceGroupId: String }

  connect() {
    this.reloadTimer = null
    this.detailReloadTimer = null
    this.consumer = getConsumer(this.spaceGroupIdValue || "default")
    this.subscription = this.consumer.subscriptions.create("SupportChannel", {
      received: (data) => this.#handleMessage(data),
    })
  }

  disconnect() {
    if (this.reloadTimer) {
      clearTimeout(this.reloadTimer)
      this.reloadTimer = null
    }
    if (this.detailReloadTimer) {
      clearTimeout(this.detailReloadTimer)
      this.detailReloadTimer = null
    }
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  #handleMessage(data) {
    console.debug(
      "[support--live-updates] Received:",
      data.event,
      data.ticket_id,
    )

    const triggeredBySelf =
      !!data.triggered_by && data.triggered_by === this.userIdValue

    switch (data.event) {
      case "ticket_created":
        this.#scheduleListReload()
        break
      case "ticket_updated":
      case "status_changed":
        // Always refresh the list — the status bar / assignee shown on each
        // card need to update even for the user who triggered the change,
        // since their Turbo Stream response only refreshed the detail panel.
        this.#scheduleListReload()
        // Refresh the detail panel too when viewing the ticket, but skip it
        // for triggeredBySelf since their detail UI is already up-to-date.
        if (this.#isViewingTicket(data.ticket_id) && !triggeredBySelf) {
          this.#scheduleDetailReload()
        }
        break
      case "comment_added":
        // Skip our own comments to avoid reloading right after we just
        // posted one in this tab.
        if (triggeredBySelf) break
        this.#scheduleListReload()
        if (this.#isViewingTicket(data.ticket_id)) {
          this.#scheduleDetailReload()
        }
        break
      case "ai_triage_complete":
      case "ai_summary_ready":
        // AI results arrived — refresh the ticket detail to show insights/summary
        if (this.#isViewingTicket(data.ticket_id)) {
          this.#scheduleDetailReload()
        }
        break
      default:
        break
    }
  }

  #isViewingTicket(ticketId) {
    const path = window.location.pathname
    return (
      path.includes(`/support/tickets/${ticketId}`) ||
      path.includes(`/support/portal/tickets/${ticketId}`)
    )
  }

  // Debounced so a burst of ActionCable events triggers a single frame reload.
  // List events silently no-op when the `tickets-list` frame is absent (the
  // controller is also mounted on pages without a list, e.g. the portal show),
  // since reloading there would surprise a user who isn't browsing the list.
  #scheduleListReload() {
    if (this.reloadTimer) return
    this.reloadTimer = setTimeout(() => {
      this.reloadTimer = null
      this.#reloadFrame("tickets-list", { fallbackToFullReload: false })
    }, RELOAD_DEBOUNCE_MS)
  }

  // Detail events fall back to a full page reload when the `ticket-detail`
  // frame is absent (e.g. the portal show page), so the user still sees the
  // update instead of silently missing it.
  #scheduleDetailReload() {
    if (this.detailReloadTimer) return
    this.detailReloadTimer = setTimeout(() => {
      this.detailReloadTimer = null
      this.#reloadFrame("ticket-detail", { fallbackToFullReload: true })
    }, RELOAD_DEBOUNCE_MS)
  }

  #reloadFrame(frameId, { fallbackToFullReload }) {
    const frame = document.getElementById(frameId)
    if (!frame) {
      if (fallbackToFullReload) window.location.reload()
      return
    }
    // Frames are server-rendered without `src`, so Turbo's `reload()` is a
    // no-op the first time. Setting `src` triggers Turbo to fetch and swap
    // the frame's contents; subsequent calls hit `reload()` directly.
    if (frame.src) {
      frame.reload()
    } else {
      frame.src = window.location.href
    }
  }
}
